/**
 * Service pack codes and overlay codes.
 *
 * Contract: docs/source-of-truth/worker-service-execution-matrix.md ("Pack key"
 * and "Overlay applicability").
 *
 * A pack is the base execution contract for an exact service. An overlay is a
 * conditional addition that applies only when its predicate matches. Neither is
 * inferred from a label: there is deliberately no `GENERIC_CLINICAL` or
 * `GENERIC_HOME_CARE` pack to fall back to.
 */

// ---------------------------------------------------------------------------
// Packs
// ---------------------------------------------------------------------------

export const SERVICE_PACK_CODES = [
  'NURSING_CLINICAL',
  'DELEGATED_CLINICAL_SUPPORT',
  'CLINICAL_ITEM',
  'ALLIED_PROFESSIONAL',
  'ALLIED_ASSISTANT',
  'THERAPEUTIC_PROFESSIONAL',
  'NUTRITION_ITEM',
  'CARE_MANAGEMENT',
  'PERSONAL_CARE_ADL',
  'MEDICATION_SELF_ADMIN_ASSIST',
  'CONTINENCE_NONCLINICAL',
  'SOCIAL_GROUP',
  'SOCIAL_INDIVIDUAL',
  'PERSONAL_AFFAIRS',
  'PERSONAL_AFFAIRS_EXPENSE',
  'RESPITE',
  'TRANSPORT_DIRECT',
  'TRANSPORT_INDIRECT',
  'AT_HM_LIFECYCLE',
  'DOMESTIC',
  'MAINTENANCE_WORK',
  'MAINTENANCE_EXPENSE',
  'MEAL_PREPARATION',
  'MEAL_DELIVERY',
] as const;

export type ServicePackCode = (typeof SERVICE_PACK_CODES)[number];

const SERVICE_PACK_CODE_SET: ReadonlySet<string> = new Set(SERVICE_PACK_CODES);

export function isServicePackCode(value: string | null | undefined): value is ServicePackCode {
  return typeof value === 'string' && SERVICE_PACK_CODE_SET.has(value);
}

/**
 * Packs whose work is delegated from a higher professional authority. These may
 * never inherit the delegator's registration: they require an explicit task,
 * competency, delegation record and named supervisor (Phase 0 decision 4).
 */
const DELEGATED_PACKS: ReadonlySet<ServicePackCode> = new Set([
  'DELEGATED_CLINICAL_SUPPORT',
  'ALLIED_ASSISTANT',
]);

export function requiresExplicitDelegation(pack: ServicePackCode): boolean {
  return DELEGATED_PACKS.has(pack);
}

/**
 * Packs whose authority is a named profession held by the worker.
 *
 * This is deliberately not "the clinical category" — matrix publication gate 6
 * forbids a blanket `clinical → AHPRA` shortcut. Aboriginal and Torres Strait
 * Islander health workers, counsellors, dietitians, art therapists, remedial
 * masseuses and diversional therapists sit in these packs and are not all
 * AHPRA-registered professions. The exact credential requirement is per
 * service, resolved from configuration, not implied by pack membership.
 */
const PROFESSION_BOUND_PACKS: ReadonlySet<ServicePackCode> = new Set([
  'NURSING_CLINICAL',
  'ALLIED_PROFESSIONAL',
  'THERAPEUTIC_PROFESSIONAL',
]);

export function isProfessionBoundPack(pack: ServicePackCode): boolean {
  return PROFESSION_BOUND_PACKS.has(pack);
}

/**
 * Packs that carry authorised money evidence (a receipt or pass-through
 * amount) as a source fact. Recording the amount seen is permitted; choosing
 * the claim item, price, contribution or funding source never is.
 */
const EXPENSE_EVIDENCE_PACKS: ReadonlySet<ServicePackCode> = new Set([
  'PERSONAL_AFFAIRS_EXPENSE',
  'MAINTENANCE_EXPENSE',
]);

export function capturesReceiptEvidence(pack: ServicePackCode): boolean {
  return EXPENSE_EVIDENCE_PACKS.has(pack);
}

// ---------------------------------------------------------------------------
// Overlays
// ---------------------------------------------------------------------------

export const OVERLAY_CODES = [
  'PALLIATIVE_END_OF_LIFE',
  'RESTORATIVE_CARE',
  'MANUAL_HANDLING_TRANSFER',
  'MEDICATION_ASSISTANCE',
  'SWALLOW_TEXTURE_ALLERGY',
  'INFECTION_PRECAUTIONS',
  'BSP_RESTRICTIVE_PRACTICE',
  'FALLS',
  'WOUND',
  'COGNITIVE_WANDERING',
  'LANGUAGE_CULTURAL',
  'HOME_HAZARD',
  'FIRST_BASELINE',
  'NEW_MUST_READ',
] as const;

export type OverlayCode = (typeof OVERLAY_CODES)[number];

const OVERLAY_CODE_SET: ReadonlySet<string> = new Set(OVERLAY_CODES);

export function isOverlayCode(value: string | null | undefined): value is OverlayCode {
  return typeof value === 'string' && OVERLAY_CODE_SET.has(value);
}

/**
 * Overlays are never implied by a category, a label or a diagnosis. Each must
 * be switched on by an active, applicable record. `PALLIATIVE_END_OF_LIFE` in
 * particular is a pathway overlay and never a service code, and it does not
 * grant professional scope or turn every visit into a clinical visit.
 */
const OVERLAY_REQUIRES_ACTIVE_RECORD: ReadonlySet<OverlayCode> = new Set(OVERLAY_CODES);

export function overlayRequiresActiveRecord(overlay: OverlayCode): boolean {
  return OVERLAY_REQUIRES_ACTIVE_RECORD.has(overlay);
}

/**
 * Overlays that actually condition an evidence rule today.
 *
 * Declaring an overlay and enforcing one are different things, and the gap
 * between them is dangerous in one direction only: a coordinator switches on a
 * safeguard, the system changes nothing, and a clinician believes a control is
 * live. An absent overlay is honest; an inert one is a false assurance.
 *
 * `PALLIATIVE_END_OF_LIFE` is the case that prompted this. It is declared, it
 * is switchable, and it conditions nothing — while its own contract says it is
 * a pathway marker that must not turn every visit into a clinical visit. Both
 * review engines independently called leaving it switchable-and-inert worse
 * than removing it, and neither would invent field conditions without a
 * clinical ruling.
 *
 * The code stays in `OVERLAY_CODES` so the concept is not lost and a later
 * wiring needs no rename. It is simply not offered as an active safeguard. A
 * guard test keeps this set honest against the invariant module.
 */
export const ENFORCEABLE_OVERLAY_CODES: readonly OverlayCode[] = ['SWALLOW_TEXTURE_ALLERGY'];

const ENFORCEABLE_OVERLAY_SET: ReadonlySet<string> = new Set(ENFORCEABLE_OVERLAY_CODES);

/**
 * Whether switching this overlay on changes what the system will accept.
 *
 * Consult before offering an overlay as a safeguard in any UI. False means the
 * concept exists but nothing enforces it yet — surface it as care context, not
 * as a control.
 */
export function overlayIsEnforceable(overlay: OverlayCode): boolean {
  return ENFORCEABLE_OVERLAY_SET.has(overlay);
}
